import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * Custom hook to manage fetching and modifying users with pagination and search.
 */
export const useUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0); // 0-indexed for frontend compatibility
    const [searchTerm, setSearchTerm] = useState('');

    const fetchUsers = useCallback(async (currentPage = page, search = searchTerm) => {
        try {
            setLoading(true);
            const pageParam = `page=${currentPage + 1}`;
            const searchParam = search ? `search=${encodeURIComponent(search)}` : '';
            const queryParams = [pageParam, searchParam].filter(Boolean).join('&');

            const response = await axios.get(`/api/users/?${queryParams}`);
            if (response.data && response.data.results) {
                setUsers(response.data.results);
                setCount(response.data.count);
            } else {
                setUsers(response.data || []);
                setCount(response.data ? response.data.length : 0);
            }
            setError('');
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    }, [page, searchTerm]);

    const createUser = async (userData) => {
        try {
            setError('');
            const response = await axios.post('/api/users/', userData);
            await fetchUsers(page, searchTerm);
            setSuccess('Usuario creado exitosamente');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error ||
                Object.values(err.response?.data || {})[0]?.[0] ||
                'Error al crear usuario';
            setError(msg);
            throw err;
        }
    };

    const updateUser = async (userId, userData) => {
        try {
            setError('');
            const response = await axios.patch(`/api/users/${userId}/`, userData);
            await fetchUsers(page, searchTerm);
            setSuccess('Usuario actualizado exitosamente');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error ||
                Object.values(err.response?.data || {})[0]?.[0] ||
                'Error al actualizar usuario';
            setError(msg);
            throw err;
        }
    };

    const deleteUser = async (userId) => {
        try {
            setError('');
            await axios.delete(`/api/users/${userId}/`);
            await fetchUsers(page, searchTerm);
            setSuccess('Usuario eliminado exitosamente');
        } catch (err) {
            setError('Error al eliminar usuario');
            throw err;
        }
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            fetchUsers(page, searchTerm);
        }, 300);
        return () => clearTimeout(delayDebounce);
    }, [page, searchTerm, fetchUsers]);

    return {
        users,
        loading,
        error,
        success,
        count,
        page,
        setPage,
        searchTerm,
        setSearchTerm: (term) => {
            setSearchTerm(term);
            setPage(0); // Reset page on search change
        },
        setError,
        setSuccess,
        fetchUsers,
        createUser,
        updateUser,
        deleteUser,
    };
};
